import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

interface AccessibilityContextType {
	highContrastMode: boolean;
	reducedMotion: boolean;
	toggleHighContrast: () => void;
	toggleReducedMotion: () => void;
}

const AccessibilityContext = createContext<AccessibilityContextType | undefined>(undefined);

export const AccessibilityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [highContrastMode, setHighContrastMode] = useState(false);
	const [reducedMotion, setReducedMotion] = useState(false);

	const toggleHighContrast = useCallback(() => {
		setHighContrastMode((prevValue) => {
			const newValue = !prevValue;
			try {
				localStorage.setItem('highContrastMode', newValue.toString());
			} catch (error) {
				// eslint-disable-next-line no-console
				console.warn('Failed to save high contrast preference to localStorage:', error);
			}
			return newValue;
		});
	}, []);

	const toggleReducedMotion = useCallback(() => {
		setReducedMotion((prevValue) => {
			const newValue = !prevValue;
			try {
				localStorage.setItem('reducedMotion', newValue.toString());
			} catch (error) {
				// eslint-disable-next-line no-console
				console.warn('Failed to save reduced motion preference to localStorage:', error);
			}
			return newValue;
		});
	}, []);

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.altKey) {
				switch (event.code) {
					case 'KeyH':
						event.preventDefault();
						toggleHighContrast();
						break;
					case 'KeyM':
						event.preventDefault();
						toggleReducedMotion();
						break;
				}
			}
		};

		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [toggleHighContrast, toggleReducedMotion]);

	// Apply CSS custom properties when preferences change
	useEffect(() => {
		document.documentElement.style.setProperty('--high-contrast', highContrastMode ? '1' : '0');
		document.documentElement.style.setProperty('--reduced-motion', reducedMotion ? '1' : '0');
		document.documentElement.setAttribute('data-high-contrast', String(highContrastMode));
		document.documentElement.setAttribute('data-reduced-motion', String(reducedMotion));
	}, [highContrastMode, reducedMotion]);

	useEffect(() => {
		let savedHighContrast: string | null = null;
		let savedReducedMotion: string | null = null;
		try {
			savedHighContrast = localStorage.getItem('highContrastMode');
			savedReducedMotion = localStorage.getItem('reducedMotion');
		} catch (error) {
			// eslint-disable-next-line no-console
			console.warn('Failed to read accessibility preferences from localStorage:', error);
		}

		if (savedHighContrast !== null) {
			setHighContrastMode(savedHighContrast === 'true');
		}
		if (savedReducedMotion !== null) {
			setReducedMotion(savedReducedMotion === 'true');
		}

		if (window.matchMedia) {
			const highContrastQuery = window.matchMedia('(prefers-contrast: high)');
			const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

			if (savedHighContrast === null) {
				setHighContrastMode(highContrastQuery.matches);
			}
			if (savedReducedMotion === null) {
				setReducedMotion(reducedMotionQuery.matches);
			}

			const handleHighContrastChange = (e: MediaQueryListEvent) => {
				try {
					if (!localStorage.getItem('highContrastMode')) {
						setHighContrastMode(e.matches);
					}
				} catch (error) {
					// eslint-disable-next-line no-console
					console.warn('Failed to check localStorage for high contrast preference:', error);
					setHighContrastMode(e.matches);
				}
			};
			const handleReducedMotionChange = (e: MediaQueryListEvent) => {
				try {
					if (!localStorage.getItem('reducedMotion')) {
						setReducedMotion(e.matches);
					}
				} catch (error) {
					// eslint-disable-next-line no-console
					console.warn('Failed to check localStorage for reduced motion preference:', error);
					setReducedMotion(e.matches);
				}
			};

			highContrastQuery.addEventListener('change', handleHighContrastChange);
			reducedMotionQuery.addEventListener('change', handleReducedMotionChange);
			return () => {
				highContrastQuery.removeEventListener('change', handleHighContrastChange);
				reducedMotionQuery.removeEventListener('change', handleReducedMotionChange);
			};
		}
	}, []);

	const value = useMemo(
		() => ({
			highContrastMode: Boolean(highContrastMode),
			reducedMotion: Boolean(reducedMotion),
			toggleReducedMotion,
			toggleHighContrast,
		}),
		[highContrastMode, reducedMotion, toggleReducedMotion, toggleHighContrast]
	);

	return <AccessibilityContext.Provider value={value}>{children}</AccessibilityContext.Provider>;
};

export const useAccessibility = () => {
	const context = useContext(AccessibilityContext);
	if (context === undefined) {
		throw new Error('useAccessibility must be used within an AccessibilityProvider');
	}
	return context;
};
